/**
 * ActivityPanel — Activity selection during planning phase.
 * Card-based layout with lock-in confirmation.
 */

import { useState } from 'react';
import {
  Footprints,
  Compass,
  Search,
  Eye,
  EyeOff,
  Hand,
  Users,
  Package,
  Sparkles,
  Clock,
  Hourglass,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StretchActivity } from '@omen-quest/stretch-engine';

// ============================================================================
// ACTIVITY DEFINITIONS
// ============================================================================

interface ActivityDef {
  type: StretchActivity['type'];
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const ACTIVITIES: ActivityDef[] = [
  { type: 'move', label: 'Move', description: 'Move through the dungeon', icon: Footprints, color: 'text-blue-400' },
  { type: 'explore', label: 'Explore', description: 'Move cautiously, expanded vision', icon: Compass, color: 'text-cyan-400' },
  { type: 'search', label: 'Search', description: 'Search area for hidden things', icon: Search, color: 'text-yellow-400' },
  { type: 'keep_watch', label: 'Keep Watch', description: 'Prevent surprise, free reaction', icon: Eye, color: 'text-green-400' },
  { type: 'move_quietly', label: 'Sneak', description: 'Stealth movement at half speed', icon: EyeOff, color: 'text-purple-400' },
  { type: 'interact', label: 'Interact', description: 'Interact with object or entity', icon: Hand, color: 'text-orange-400' },
  { type: 'help', label: 'Help Out', description: 'Grant advantage to an ally', icon: Users, color: 'text-pink-400' },
  { type: 'loot', label: 'Loot', description: 'Collect discovered valuables', icon: Package, color: 'text-amber-400' },
  { type: 'cast_spell', label: 'Cast Spell', description: 'Ritual or extended magic', icon: Sparkles, color: 'text-indigo-400' },
  { type: 'take_your_time', label: 'Take Your Time', description: 'Auto-succeed on a task', icon: Clock, color: 'text-teal-400' },
  { type: 'wait', label: 'Wait', description: 'Pass — free RP time', icon: Hourglass, color: 'text-neutral-400' },
];

// ============================================================================
// COMPONENT
// ============================================================================

interface ActivityPanelProps {
  selectedActivity: StretchActivity | null;
  onSelectActivity: (activity: StretchActivity) => void;
  onConfirm: () => void;
  disabled?: boolean;
  isCommitting?: boolean;
  playerNames?: Array<{ id: string; name: string }>;
  className?: string;
}

export function ActivityPanel({
  selectedActivity,
  onSelectActivity,
  onConfirm,
  disabled = false,
  isCommitting = false,
  playerNames = [],
  className,
}: ActivityPanelProps) {
  const [helpTarget, setHelpTarget] = useState<string | null>(null);
  const [takeYourTimeDesc, setTakeYourTimeDesc] = useState('');

  const handleCardClick = (def: ActivityDef) => {
    if (disabled) return;

    switch (def.type) {
      case 'help':
        // Need to pick a target — handled by sub-selection
        if (playerNames.length > 0) {
          onSelectActivity({ type: 'help', targetPlayerId: playerNames[0].id });
        }
        break;
      case 'take_your_time':
        onSelectActivity({ type: 'take_your_time', description: takeYourTimeDesc || 'Taking time' });
        break;
      case 'move':
        onSelectActivity({ type: 'move', path: [] });
        break;
      case 'explore':
        onSelectActivity({ type: 'explore' });
        break;
      case 'move_quietly':
        onSelectActivity({ type: 'move_quietly' });
        break;
      case 'interact':
        onSelectActivity({ type: 'interact', x: 0, y: 0 });
        break;
      default:
        onSelectActivity({ type: def.type } as StretchActivity);
        break;
    }
  };

  const selectedType = selectedActivity?.type ?? null;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Header */}
      <div className="text-center">
        <h3 className="text-sm font-semibold text-white">Choose Activity</h3>
        <p className="text-xs text-neutral-500 mt-0.5">What will you do this stretch?</p>
      </div>

      {/* Activity grid */}
      <div className="grid grid-cols-3 gap-2">
        {ACTIVITIES.map(def => {
          const Icon = def.icon;
          const isSelected = selectedType === def.type;
          return (
            <button
              key={def.type}
              onClick={() => handleCardClick(def)}
              disabled={disabled}
              className={cn(
                'flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors text-center',
                isSelected
                  ? 'bg-white/10 border-white/30 ring-1 ring-white/20'
                  : 'bg-neutral-900/50 border-white/5 hover:bg-neutral-800/50 hover:border-white/10',
                disabled && 'opacity-50 cursor-not-allowed',
              )}
            >
              <Icon className={cn('w-5 h-5', isSelected ? def.color : 'text-neutral-500')} />
              <span className={cn(
                'text-xs font-medium',
                isSelected ? 'text-white' : 'text-neutral-400',
              )}>
                {def.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Help target selector */}
      {selectedType === 'help' && playerNames.length > 0 && (
        <div className="space-y-1">
          <label className="text-xs text-neutral-400">Help who?</label>
          <div className="flex gap-1.5">
            {playerNames.map(p => (
              <button
                key={p.id}
                onClick={() => {
                  setHelpTarget(p.id);
                  onSelectActivity({ type: 'help', targetPlayerId: p.id });
                }}
                className={cn(
                  'px-2 py-1 text-xs rounded border transition-colors',
                  helpTarget === p.id
                    ? 'bg-pink-900/40 border-pink-700/50 text-pink-300'
                    : 'bg-neutral-800/50 border-white/5 text-neutral-400 hover:text-white',
                )}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Take your time description */}
      {selectedType === 'take_your_time' && (
        <div className="space-y-1">
          <label className="text-xs text-neutral-400">What task?</label>
          <input
            type="text"
            value={takeYourTimeDesc}
            onChange={(e) => {
              setTakeYourTimeDesc(e.target.value);
              onSelectActivity({ type: 'take_your_time', description: e.target.value || 'Taking time' });
            }}
            placeholder="Pick the lock, decipher the runes..."
            className="w-full px-2 py-1.5 text-xs bg-neutral-800/50 border border-white/10 rounded text-white placeholder-neutral-600 focus:outline-none focus:border-white/20"
          />
        </div>
      )}

      {/* Selected activity description */}
      {selectedActivity && (
        <div className="text-xs text-neutral-400 bg-neutral-800/30 rounded-lg px-3 py-2 border border-white/5">
          {ACTIVITIES.find(a => a.type === selectedType)?.description}
        </div>
      )}

      {/* Confirm button */}
      <button
        onClick={onConfirm}
        disabled={!selectedActivity || disabled || isCommitting}
        className={cn(
          'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors',
          selectedActivity && !disabled
            ? 'bg-white/10 text-white hover:bg-white/15 border border-white/20'
            : 'bg-neutral-800/30 text-neutral-600 border border-white/5 cursor-not-allowed',
        )}
      >
        <Check className="w-4 h-4" />
        {isCommitting ? 'Committing...' : 'Lock In'}
      </button>
    </div>
  );
}
